<template>
  <div
    class="rounded-lg bg-surface border-main br-root-2 py-6 h-full"
    :class="hasKycDisabledClass"
  >
    <div class="border-b br-root-2">
      <div class="flex justify-between lg:px-10 px-3 pb-5">
        <div>
          <p class="font-bold text-fg-default text-lg">Transactions</p>
          <p class="font-normal lg:w-full text-fg-muted text-xs">
            This shows your latest transactions.
          </p>
        </div>
        <tooltip
          :disabled="hasCompletedKyc"
          placement="bottom"
          :content="disabledKycTooltipContent"
        >
          <div
            class="br-root-1 border rounded-lg w-16 lg:w-28 font-medium text-xs text-fg-default flex justify-center items-center cursor-pointer"
            @click="goToTransactions"
          >
            See all
          </div>
        </tooltip>
      </div>
    </div>
    <div>
      <div
        v-if="noTransactions"
        class="flex justify-center text-center mt-8 font-bold text-lg text-black-2"
      >
        <transaction-empty-state use="dashboard">
          <h3 class="text-sm md:text-base text-fg-default font-semibold">
            No Transactions
          </h3>
          <p class="font-light text-xs text-fg-muted md:text-sm my-3 md:mt-5">
            Your transactions show up here
          </p>
        </transaction-empty-state>
      </div>
      <div
        v-else
        v-for="(transaction, index) in latestTransactions"
        :key="index"
        class="md:px-7"
      >
        <div
          class="border-b br-root-2 cursor-pointer"
          @click="showTransaction(transaction)"
        >
          <div class="flex justify-between py-4 px-5">
            <div class="flex items-center gap-x-3 md:gap-x-6">
              <arrow-down :transactionType="transaction.type" />
              <div>
                <p
                  class="md:text-sm text-xs font-bold mb-2 capitalize text-fg-default"
                >
                  {{ transaction.type }} ({{
                    transaction.method.replace(/_/g, ' ')
                  }})
                </p>
                <p class="text-fg-muted text-xs font-normal">
                  {{ tooltipDateContent(transaction.createdAt) }}
                </p>
              </div>
            </div>
            <div class="flex items-center md:gap-x-8 gap-x-2">
              <div class="flex flex-col justify-end">
                <p
                  class="text-right md:text-sm text-xs mb-2 text-fg-default font-bold"
                >
                  {{
                    transaction.type === $transactionType.DEPOSIT ? '+' : '-'
                  }}
                  {{
                    transaction.type === $transactionType.WITHDRAWAL
                      ? parseMoneyAll(
                          transaction.amount +
                            (transaction.businessCommission || 0),
                          transaction.currency
                        )
                      : transaction.method === $transactionType.ASSET_TRANSFER
                      ? parseMoneyAll(
                          transaction.depositedAmount +
                            (transaction.businessCommission || 0),
                          transaction.depositedCurrency
                        )
                      : transaction.method === $transactionType.LOCAL_TRANSFER
                      ? parseMoneyAll(
                          transaction.depositedAmount +
                            (transaction.businessCommission || 0),
                          transaction.depositedCurrency
                        )
                      : parseMoneyAll(
                          (transaction.transferredAmount ||
                            transaction.depositedAmount) +
                            (transaction.businessCommission || 0),
                          transaction.depositedCurrency
                        )
                  }}
                </p>
                <div
                  :class="statusClass(transaction.status)"
                  class="px-2 flex justify-center capitalize rounded-lg py-0.5 text-xs font-medium w-fit self-end"
                >
                  {{ formatStatusName(transaction.status) }}
                </div>
              </div>
              <arrow-right-icon />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import statusClass from '@/mixins/global.js'
import app from '@/mixins/app.js'
import { ParseLocalTime, ParseMoneyAll } from '@/utils/helpers.js'
import formatStatusName from '@/mixins/transactions.js'

export default {
  mixins: [app, statusClass, formatStatusName],
  components: {
    ArrowDown: () => import('@/assets/v2/icons/ArrowDown.vue'),
    ArrowRightIcon: () => import('@/assets/icons/ArrowRightIcon.vue'),
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue')
  },
  data () {
    return {
      isLoading: false,
      disabledKycTooltipContent:
        'KYC is yet to be approved, currently under review'
    }
  },
  methods: {
    parseLocalTime (val) {
      return ParseLocalTime(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    showTransaction (transaction) {
      this.$emit('showTransaction', transaction)
    },
    goToTransactions () {
      this.$router.push('/transactions')
    },
    async getTransactions () {
      const payload = {
        page: 1,
        size: 10,
        sort_by: 'created_at',
        sort_direction_desc: true,
        status: '',
        type: '',
        from: '',
        to: ''
      }
      await this.$store
        .dispatch('transactions/getTransactions', payload)
        .then(() => {
          this.isLoading = false
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
    }
  },
  computed: {
    hasKycDisabledClass () {
      return this.hasCompletedKyc
        ? 'opacity-100 hover:opacity-80'
        : 'cursor-not-allowed opacity-80 hover:opacity-70'
    },
    hasCompletedKyc () {
      return this.$store.state.auth.business.completedKyc
    },
    latestTransactions () {
      if (
        !this.$store.state.transactions.transactions ||
        !this.$store.state.transactions.transactions.items
      ) {
        return []
      }
      return this.$store.state.transactions.transactions.items.slice(0, 4)
    },
    noTransactions () {
      return this.latestTransactions.length < 1
    },
    transactions () {
      return this.$store.state.transactions.transactions.items
    }
  },
  async mounted () {
    await this.getTransactions()
  }
}
</script>
